import { AUTH_URL } from './config.js';

interface WsTicketResponse {
  ticket: string;
}

export interface DiscordTicketResponse {
  accessToken: string;
  ticket: string;
  accountId: string;
}

export async function getWsTicket(): Promise<string | null> {
  try {
    const response = await fetch(AUTH_URL + '/auth/ws-ticket', {
      method: 'POST',
      credentials: 'include',
    });

    if (response.status === 401) {
      return null;
    }

    const body = (await response.json()) as WsTicketResponse;
    return body.ticket;
  } catch {
    return null;
  }
}

interface PresenceTokenResponse {
  token: string;
}

/**
 * Fetches a short-lived (5-minute) presence-capability JWT from
 * `POST /auth/presence-token` — `aud: "presence"`, listing the ids the caller
 * may query live status for. Distinct from `getWsTicket`: that authenticates
 * a WebSocket connection's identity; this authorizes what it may ask about
 * once connected. Same failure shape as `getWsTicket` — `null` on any
 * failure, signed-out included.
 */
export async function getPresenceToken(): Promise<string | null> {
  try {
    const response = await fetch(AUTH_URL + '/auth/presence-token', {
      method: 'POST',
      credentials: 'include',
    });

    if (response.status === 401) {
      return null;
    }

    const body = (await response.json()) as PresenceTokenResponse;
    return body.token;
  } catch {
    return null;
  }
}

/**
 * Exchanges a Discord OAuth authorization code for the Discord access token plus
 * the same HS256 ticket `getWsTicket` returns. No session cookie is involved —
 * the code is the only credential — so this call is deliberately uncredentialed.
 */
export async function getDiscordWsTicket(
  code: string,
  clientId: string,
): Promise<DiscordTicketResponse | null> {
  try {
    const response = await fetch(AUTH_URL + '/auth/discord/ws-ticket', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, clientId }),
    });

    if (!response.ok) {
      return null;
    }

    return (await response.json()) as DiscordTicketResponse;
  } catch {
    return null;
  }
}
